import React from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { AlertTriangle, XCircle, FileText } from 'lucide-react';
import { useDashboardData } from '../hooks/useDashboardData';
import { DashboardHeader } from '../components/dashboard/DashboardHeader';
import { ConfigForm } from '../components/dashboard/ConfigForm';
import { UploadZone } from '../components/dashboard/UploadZone';
import { BillsList } from '../components/dashboard/BillsList';
import { HistoricalAnalyses } from '../components/dashboard/HistoricalAnalyses';
import { AnalysisResult } from '../components/dashboard/AnalysisResult';
import { MarketDataReminder } from '../components/MarketDataReminder';

/**
 * Pagina principale della dashboard Bolletto.
 * Compone i sotto-componenti e connette lo stato dal hook `useDashboardData`.
 */
export default function DashboardPage() {
  const data = useDashboardData();

  const isConfigComplete = !!(data.leadSource && data.customerType && data.targetSavings);

  return (
    <div className="min-h-screen bg-[#F4F7F4]">
      <DashboardHeader />

      <main className="max-w-7xl mx-auto px-6 py-8">
        <MarketDataReminder variant="dashboard" />

        {/* Errore globale */}
        <AnimatePresence>
          {data.error && (
            <motion.div
              initial={{ opacity: 0, y: -10 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -10 }}
              className="mb-6 p-4 bg-red-50 border border-red-200 rounded-2xl text-red-600 flex items-start gap-3"
              data-testid="error-message"
            >
              <AlertTriangle className="w-5 h-5 flex-shrink-0 mt-0.5" />
              <p>{data.error}</p>
              <button onClick={() => data.setError('')} className="ml-auto">
                <XCircle className="w-5 h-5" />
              </button>
            </motion.div>
          )}
        </AnimatePresence>

        <div className="grid grid-cols-1 lg:grid-cols-12 gap-6">
          {/* Colonna sinistra — configurazione, upload, bollette, storico */}
          <div className="lg:col-span-5 space-y-6">
            <ConfigForm
              isAdmin={data.isAdmin}
              accountName={data.accountName}
              accountRole={data.accountRole}
              allowedCustomerTypes={data.allowedCustomerTypes}
              leadSources={data.leadSources}
              leadSource={data.leadSource}
              onLeadSourceChange={data.setLeadSource}
              customerType={data.customerType}
              onCustomerTypeChange={data.setCustomerType}
              targetSavings={data.targetSavings}
              onTargetSavingsChange={data.setTargetSavings}
            />

            <UploadZone
              isConfigComplete={isConfigComplete}
              uploading={data.ui.uploading}
              dropzone={data.dropzone}
            />

            <BillsList
              groupedBills={data.groupedBills}
              openFolders={data.ui.openFolders}
              onToggleFolder={(key) => data.dispatchUI({ type: 'TOGGLE_FOLDER', key })}
              selectedBill={data.selectedBill}
              analyzingBillId={data.ui.analyzing}
              hasCustomerType={!!data.customerType}
              analyzedBillIds={data.analyzedBillIds}
              analysesCountByBillId={data.analysesCountByBillId}
              onAnalyze={data.handleAnalyze}
              onDelete={data.handleDeleteBill}
            />

            <HistoricalAnalyses
              groupedAnalyses={data.groupedAnalyses}
              openFolders={data.ui.openHistoryFolders}
              onToggleFolder={(key) => data.dispatchUI({ type: 'TOGGLE_HISTORY_FOLDER', key })}
              selectedIds={data.ui.selectedHistoryIds}
              onToggleSelection={(id, e) => { e.stopPropagation(); data.dispatchUI({ type: 'TOGGLE_HISTORY_SELECTION', id }); }}
              onToggleSelectAll={(analyses) => data.dispatchUI({ type: 'TOGGLE_SELECT_ALL_IN_FOLDER', ids: analyses.map((a) => a.id) })}
              currentAnalysis={data.analysis}
              isHistorical={data.isHistorical}
              onViewAnalysis={data.handleViewSavedAnalysis}
              onBatchPdf={data.handleBatchPdf}
              onBatchDelete={data.handleBatchDelete}
              batchDownloading={data.ui.batchDownloading}
              batchDeleting={data.ui.batchDeleting}
              totalCount={data.savedAnalyses.length}
            />
          </div>

          {/* Colonna destra — risultato analisi */}
          <div className="lg:col-span-7">
            <AnimatePresence mode="wait">
              {data.analysis && !data.analysis.error ? (
                <motion.div key="analysis" initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: -20 }} className="space-y-6">
                  <AnalysisResult
                    analysisRef={data.analysisRef}
                    analysis={data.analysis}
                    isHistorical={data.isHistorical}
                    downloading={data.ui.downloading}
                    onDownloadPdf={data.handleDownloadPdf}
                    manualCte={data.manualCte}
                    onManualCteChange={data.setManualCte}
                    availableCtes={data.availableCtes}
                    updatingCte={data.ui.updatingCte}
                    onUpdateCte={data.handleManualCteUpdate}
                    onUpdateAnnualConsumption={data.handleUpdateAnnualConsumption}
                    onEditExtracted={data.handleEditExtracted}
                    isFinalized={data.isFinalized}
                    finalizing={data.ui.finalizing}
                    onFinalize={data.handleFinalize}
                  />
                </motion.div>
              ) : data.analysis?.error ? (
                <motion.div key="error" initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} className="el-card p-8 text-center">
                  <AlertTriangle className="w-16 h-16 mx-auto text-orange-500 mb-4" />
                  <h3 className="text-xl font-medium text-[#1B291B] mb-2">Analisi Manuale Richiesta</h3>
                  <p className="text-[#5C6E5C] max-w-md mx-auto">{data.analysis.error.message}</p>
                  <p className="text-sm text-[#5C6E5C] mt-4">
                    Codice SII: <code className="bg-[#F4F7F4] px-2 py-1 rounded">{data.analysis.error.sii_code}</code>
                  </p>
                </motion.div>
              ) : (
                <motion.div key="empty" initial={{ opacity: 0 }} animate={{ opacity: 1 }} className="el-card p-12 text-center" data-testid="empty-analysis-state">
                  <FileText className="w-16 h-16 mx-auto text-[#DCE3DC] mb-4" />
                  <h3 className="text-xl font-medium text-[#1B291B] mb-2">Nessuna Analisi</h3>
                  <p className="text-[#5C6E5C] max-w-md mx-auto">Carica una bolletta PDF per vedere il confronto con le tariffe Energy.Lab.</p>
                </motion.div>
              )}
            </AnimatePresence>
          </div>
        </div>
      </main>

    </div>
  );
}
